import { useSession } from "@src/appV2/Auth/api/useSession";
import { useLocation } from "react-router-dom";

import { instrumentNavigationItem } from "../../Shared/Navigation/instrumentNavigationItem";
import { useGetNavigationMenu } from "../../Shared/Navigation/navigationMenu/useGetNavigationMenu";
import {
  NavigationItem as NavigationItemParams,
  NavigationSection as NavigationSectionParams,
} from "../../Shared/Navigation/navigationMenu/types";
import { useAdminMenuPrivilegesProviderContext } from "../../Shared/Navigation/privilegesProviders/AdminMenuPrivilegesProvider";
import { useWorkerMenuPrivilegesProviderContext } from "../../Shared/Navigation/privilegesProviders/WorkerMenuPrivilegesProvider";
import { useWorkplaceMenuPrivilegesProviderContext } from "../../Shared/Navigation/privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { usePendingExtraWorkedTimeRequestCount } from "../../Shared/Navigation/usePendingExtraWorkedTimeRequestCount";
import { usePendingVisitOccurrenceCount } from "../../Shared/Navigation/usePendingVisitOccurrenceCount";
import { useUnreadChannelCount } from "../../Shared/Navigation/useUnreadChannelCount";
import { NavigationMenuItemDesktop } from "@src/appV2/redesign/AppLayout/Desktop/Navigation/MenuItem";
import { NavigationMenuSectionTitleDesktop } from "@src/appV2/redesign/AppLayout/Desktop/Navigation/MenuSectionTitle";

export interface NavigationMenuItemsDesktopProps {
  isAdmin: boolean;
  isWorkplace: boolean;
  isWorker: boolean;
}

function NavigationItem(params: NavigationItemParams) {
  const { label, iconType, isSelected, routeTo, onClick, onPreload, badgeCount } = params;

  return (
    <NavigationMenuItemDesktop
      iconType={iconType}
      isSelected={isSelected}
      routeTo={routeTo}
      onClick={onClick}
      onPreload={onPreload}
      badgeCount={badgeCount}
    >
      {label}
    </NavigationMenuItemDesktop>
  );
}

function NavigationSection(params: NavigationSectionParams) {
  const { label, items } = params;

  return (
    <>
      <NavigationMenuSectionTitleDesktop>{label}</NavigationMenuSectionTitleDesktop>
      {items.map((item) => (
        <NavigationItem key={item.label} {...item} />
      ))}
    </>
  );
}

export function NavigationMenuItemsDesktop(props: NavigationMenuItemsDesktopProps) {
  const { isAdmin, isWorker, isWorkplace } = props;

  const currentPath = location.pathname;
  const adminMenuPrivileges = useAdminMenuPrivilegesProviderContext();
  const workplaceMenuPrivileges = useWorkplaceMenuPrivilegesProviderContext();
  const workerMenuPrivileges = useWorkerMenuPrivilegesProviderContext();
  const unreadChannelCount = useUnreadChannelCount();
  const pendingExtraWorkedTimeRequestCount = usePendingExtraWorkedTimeRequestCount();
  const pendingVisitOccurrenceCount = usePendingVisitOccurrenceCount();
  const session = useSession();
  const previousLocation = useLocation();

  const navigationMenu = useGetNavigationMenu(
    isAdmin,
    adminMenuPrivileges,
    isWorker,
    workerMenuPrivileges,
    isWorkplace,
    workplaceMenuPrivileges,
    currentPath,
    { unreadChannelCount, pendingExtraWorkedTimeRequestCount, pendingVisitOccurrenceCount }
  );

  const clickContext = { isAdmin, session, previousLocation };

  return (
    <>
      {navigationMenu.items.map((item) =>
        "items" in item ? (
          <NavigationSection
            key={item.label}
            {...item}
            items={item.items.map((subItem) => instrumentNavigationItem(subItem, clickContext))}
          />
        ) : (
          <NavigationItem key={item.label} {...instrumentNavigationItem(item, clickContext)} />
        )
      )}
    </>
  );
}
